import React, { useMemo, useState, useEffect } from "react";
import { Modal } from "reactstrap";
import { Link } from "react-router-dom";
import ReactDOMServer from "react-dom/server";
import ButtonSpinner from "../../components/Buttons/ButtonSpinner";
import { buildPreviewDoc } from "./previewDoc";
import { getAllForms } from "../../utils/services/rsvp";
import FormMounted from "../AboutEvent/rsvp/FormMounted";
import "./modalPreviewV2.scss";

// ============================================================================
// V2 — Preview moderno de template (fullscreen + toggle de dispositivo).
// Estilo referência: Mailchimp / Klaviyo / Postmark.
//
// COMO REVERTER:
//   Em cada arquivo que consome este modal, trocar o import por
//   `./modalPreview` (o legado) — ou trocar `USE_MODERN_PREVIEW = true`
//   para `false` onde a flag estiver definida.
//   O arquivo antigo (modalPreview.jsx) segue intocado.
// ============================================================================

const DEVICES = [
  { id: "desktop", label: "Desktop", icon: "fa fa-desktop", width: "100%" },
  { id: "tablet", label: "Tablet", icon: "fa fa-tablet-alt", width: 768 },
  { id: "mobile", label: "Mobile", icon: "fa fa-mobile-alt", width: 375 },
];

const ModalPreviewV2 = (args) => {
  const [device, setDevice] = useState("desktop");
  const [resolvedHtml, setResolvedHtml] = useState(null);

  // Ao abrir o modal, resolve as referências data-form-id buscando a versão
  // atual de cada formulário — garante que o preview reflita edições recentes.
  useEffect(() => {
    if (!args.modal) return;
    const html = args.template?.htmlString;
    if (!html || !html.includes("data-form-id") || !args.location?.idEvent) {
      setResolvedHtml(html || "");
      return;
    }
    getAllForms({
      eventId: args.location.idEvent,
      jwt: args.token,
      offset: 1,
      limit: 100,
      search: "",
    })
      .then((response) => {
        const forms = response?.data?.forms || [];
        const formsMap = {};
        forms.forEach((form) => {
          try {
            formsMap[form.id] = ReactDOMServer.renderToString(
              <FormMounted
                form={JSON.parse(form.json)}
                saveForm={() => null}
                components={() => null}
                setForm={() => null}
              />,
            );
          } catch {}
        });
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        doc.querySelectorAll("[data-form-id]").forEach((el) => {
          const id = el.getAttribute("data-form-id");
          if (formsMap[id]) el.innerHTML = formsMap[id];
        });
        setResolvedHtml(doc.body.innerHTML);
      })
      .catch(() => setResolvedHtml(html));
  }, [args.modal, args.template?.id]);

  const srcDoc = useMemo(
    () =>
      buildPreviewDoc(
        resolvedHtml ?? args.template?.htmlString,
        args.template?.htmlJson,
      ),
    [resolvedHtml, args.template?.htmlString, args.template?.htmlJson],
  );

  const currentDevice = DEVICES.find((d) => d.id === device) || DEVICES[0];

  const editLink = args.internal ? "/event/templates" : "/html-editor";
  const editState = {
    ...args.location,
    type_template: args.template?.type,
    editMode: true,
    template: args.template,
  };

  return (
    <Modal
      isOpen={args.modal}
      toggle={args.toggle}
      className="modal-preview-v2"
      contentClassName="modal-preview-v2-content"
      backdrop
      fullscreen
    >
      <div className="mpv2-topbar">
        <div className="mpv2-topbar-left">
          <i className="fa fa-eye mpv2-topbar-icon" />
          <div>
            <div className="mpv2-topbar-title">Pré-visualização</div>
            {args.template?.name && (
              <div className="mpv2-topbar-sub">{args.template.name}</div>
            )}
          </div>
        </div>

        <div className="mpv2-topbar-devices">
          {DEVICES.map((d) => (
            <button
              key={d.id}
              type="button"
              className={`mpv2-device-btn ${device === d.id ? "active" : ""}`}
              onClick={() => setDevice(d.id)}
              title={d.label}
            >
              <i className={d.icon} />
              <span>{d.label}</span>
            </button>
          ))}
        </div>

        <div className="mpv2-topbar-actions">
          <Link to={editLink} state={editState} className="mpv2-btn-edit">
            <ButtonSpinner title="Editar Template" size={120} action="U" />
          </Link>
          <button
            type="button"
            className="mpv2-close-btn"
            onClick={args.toggle}
            title="Fechar"
            aria-label="Fechar"
          >
            <i className="fa fa-times" />
          </button>
        </div>
      </div>

      <div className="mpv2-stage">
        <div
          className="mpv2-frame"
          style={{
            width:
              typeof currentDevice.width === "number"
                ? `${currentDevice.width}px`
                : currentDevice.width,
          }}
        >
          <iframe
            title="Pré-visualização do template"
            className="mpv2-iframe"
            srcDoc={srcDoc}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ModalPreviewV2;
